import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Horse, MapPin, User } from "@phosphor-icons/react";
import { searchAll } from "../lib/api";
import { Input } from "./ui/input";

export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults(null); setOpen(false); return; }
    const timer = setTimeout(async () => {
      const data = await searchAll(query);
      setResults(data);
      setOpen(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelect = (type, id) => {
    setOpen(false);
    setQuery("");
    if (type === "horse") navigate(`/horse/${id}`);
    else if (type === "track") navigate(`/track/${id}`);
    else if (type === "jockey") navigate(`/jockey/${id}`);
  };

  return (
    <div ref={ref} className="relative w-full max-w-md" data-testid="search-bar">
      <div className="relative">
        <MagnifyingGlass weight="bold" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          data-testid="horse-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search horses, tracks, jockeys..."
          className="pl-9 h-9 text-sm font-mono border-border rounded-none bg-white focus:ring-2 focus:ring-black"
        />
      </div>

      {open && results && (
        <div className="absolute top-full left-0 right-0 bg-white border border-border border-t-0 z-50 shadow-sm max-h-80 overflow-auto">
          {results.horses?.length > 0 && (
            <div>
              <p className="mono-label text-zinc-400 px-3 py-2 bg-zinc-50 border-b border-border">Horses</p>
              {results.horses.map((h) => (
                <button
                  key={h.id}
                  data-testid={`search-result-horse-${h.id}`}
                  onClick={() => handleSelect("horse", h.id)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-2 text-sm border-b border-border/50"
                >
                  <Horse weight="bold" size={14} className="text-zinc-400" />
                  <span className="font-medium">{h.name}</span>
                  <span className="text-xs text-zinc-400">{h.trainer}</span>
                </button>
              ))}
            </div>
          )}
          {results.tracks?.length > 0 && (
            <div>
              <p className="mono-label text-zinc-400 px-3 py-2 bg-zinc-50 border-b border-border">Tracks</p>
              {results.tracks.map((t) => (
                <button
                  key={t.id}
                  data-testid={`search-result-track-${t.id}`}
                  onClick={() => handleSelect("track", t.id)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-2 text-sm border-b border-border/50"
                >
                  <MapPin weight="bold" size={14} className="text-zinc-400" />
                  <span className="font-medium">{t.name}</span>
                  <span className="text-xs text-zinc-400">{t.location}</span>
                </button>
              ))}
            </div>
          )}
          {results.jockeys?.length > 0 && (
            <div>
              <p className="mono-label text-zinc-400 px-3 py-2 bg-zinc-50 border-b border-border">Jockeys</p>
              {results.jockeys.map((j) => (
                <button
                  key={j.id}
                  data-testid={`search-result-jockey-${j.id}`}
                  onClick={() => handleSelect("jockey", j.id)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-2 text-sm border-b border-border/50"
                >
                  <User weight="bold" size={14} className="text-zinc-400" />
                  <span className="font-medium">{j.name}</span>
                  <span className="text-xs text-zinc-400">{j.nationality}</span>
                </button>
              ))}
            </div>
          )}
          {!results.horses?.length && !results.tracks?.length && !results.jockeys?.length && (
            <p className="px-3 py-4 text-sm text-zinc-400 text-center">No results found</p>
          )}
        </div>
      )}
    </div>
  );
}
